import dayjs from "dayjs"
import "dayjs/locale/ar"
import "dayjs/locale/en"
import { getAppLanguage } from "configs/useInitializeApp"

const DateTime = {
  locale: lang => {
    return dayjs.locale(lang)
  },
  formatDate: (date = null, format) => {
    if (date) {
      return dayjs(date).format(format)
    }
    return dayjs().format(format)
  },
  fromDateAndTime: (dateTime, suffix = false) => {
    const language = getAppLanguage()
    const locale = language?.locale || "en"
    const dayjsInstance = dayjs(dateTime).locale(locale)
    return suffix == true ? dayjsInstance.fromNow(true) : dayjsInstance.fromNow()
  },
  getCurrentHour: () => {
    return dayjs().hour()
  },
  setHours: hour => {
    return dayjs().hour(hour)
  },
  getCurrentMinutes: () => {
    return dayjs().minute()
  },
  setMinutes: minutes => {
    return dayjs().minute(minutes)
  },
  currentTime: () => {
    return dayjs()
  },
  isSameDay: (time1, time2) => {
    return dayjs(time1).isSame(dayjs(time2), "day")
  },
  isInSameMonth: (date1, date2) => {
    return dayjs(date1).isSame(dayjs(date2), "month")
  },
  isInSameWeek: (date1, date2) => {
    return dayjs(date1).isSame(dayjs(date2), "week")
  },
  getHourFromDate: dateAndTime => {
    return dayjs(dateAndTime).hour()
  },
  getMinutesFromDate: dateAndTime => {
    return dayjs(dateAndTime).minute()
  },
  getDateAndTime: dateAndTime => {
    return dayjs(dateAndTime)
  },
  isAfterCurrentDay: dateAndTime => {
    return dayjs(dateAndTime)?.isAfter(dayjs(), "day")
  },
  hasSameHoursandMinutes: (time1, time2) => {
    return (
      DateTime.getHourFromDate(time1) === DateTime.getHourFromDate(time2) &&
      DateTime.getMinutesFromDate(time1) === DateTime.getMinutesFromDate(time2)
    )
  },
  isBeforeCurrentDay: dateAndTime => {
    return dayjs(dateAndTime)?.isBefore(dayjs(), "day")
  },
  isBeforeCurrentDateAndTime: dateAndTime => {
    return dateAndTime?.isBefore(dayjs())
  },
  isAfterCurrentDateAndTime: dateAndTime => {
    return dateAndTime?.isAfter(dayjs())
  },
  getDaysBeforeCurrentTime: noOfDays => {
    return dayjs().subtract(noOfDays, "day")
  },
  getNumberOfDaysBetweenDates: (startTime, endTime) => {
    return endTime.diff(startTime, "day")
  },
  getDaysAfterDateProvided: (dateAndTime, noOfDays) => {
    return dayjs(dateAndTime).add(noOfDays, "day")
  },
  getMonthsBeforeCurrentTime: noOfMonths => {
    return dayjs().subtract(noOfMonths, "month")
  },
  getStartOfDay: date => {
    return date.startOf("day")
  },
  getEndOfDay: date => {
    return date.endOf("day")
  },
  isAfterMentionedDate: (dateToBeCompared, dateToBeComparedFrom) => {
    return dateToBeCompared.isAfter(dateToBeComparedFrom)
  },
  getHoursAfterDateProvided: (dateAndTime, noOfHours) => {
    return dayjs(dateAndTime).add(noOfHours, "hour")
  },
  getMonthsAfterDateProvided: (dateAndTime, noOfMonths) => {
    return dayjs(dateAndTime).add(noOfMonths, "month")
  },
  getQuarterYearAfterDateProvided: (dateAndTime, noOfQuarters) => {
    const formattedDate = dateAndTime.format("YYYY-MM-DD")
    return dayjs(formattedDate).add(noOfQuarters, "quarter")
  },
  convertDateToUnixTimeStamp: date => {
    return date.unix()
  },
  getStartOfMonth: date => {
    return date.startOf("month")
  },
  getEndOfMonth: date => {
    return date.endOf("month")
  },
  getMonthsBeforeDateProvided: (dateAndTime, noOfMonths) => {
    return dayjs(dateAndTime).subtract(noOfMonths, "month")
  },
  convertDateToString: date => {
    return date.toString()
  },
  getNumberOfMilliSecondsBetweenDates: (startTime, endTime) => {
    if (!startTime || !endTime) return 0
    return endTime.diff(startTime)
  },
  isValidDate: dateAndTime => {
    return dayjs(dateAndTime).isValid()
  },
    
  // Get duration in hours from time range
  getDurationInHours: (startDate, endDate) => {
    if (!startDate || !endDate) return null
    
    const start = dayjs(startDate)
    const end = dayjs(endDate)
    
    const durationInHours = end.diff(start, 'hour', true)
    
    // If less than 1 hour, show minutes
    if (durationInHours < 1) {
      const durationInMinutes = end.diff(start, 'minute', true)
      
      // If less than 1 minute, show seconds
      if (durationInMinutes < 1) {
        const durationInSeconds = end.diff(start, 'second')
        return `${durationInSeconds} ${durationInSeconds === 1 ? 'second' : 'seconds'}`
      }
      
      return `${Math.round(durationInMinutes)} ${Math.round(durationInMinutes) === 1 ? 'minute' : 'minutes'}`
    }
    
    return `${Math.round(durationInHours * 100) / 100} ${durationInHours === 1 ? 'hour' : 'hours'}`
  },
}

export default DateTime
